import { useCallback, useState } from 'react';
import { Table } from '@tanstack/react-table';
import { type CleosTableFeaturesType } from '../tableFeatures';

interface UseExpandedRowsResult {
  activeRows: string[];
  toggleRow: (rowId: string) => void;
  expandNext: (rowId: string) => void;
  closeAll: () => void;
}

// Includes the pathname even when tableKey is set: expanded-row state is per-entity, unlike
// tableKey's stable per-table-*type* identity (shared across entities for column persistence).
const sessionKey = (tableKey?: string) => `expandedRows:${tableKey ?? ''}:${window.location.pathname}`;

const loadFromSession = (tableKey?: string): string[] => {
  try {
    const stored = window.sessionStorage.getItem(sessionKey(tableKey));
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    window.sessionStorage.removeItem(sessionKey(tableKey));
  }
  return [];
};

const saveToSession = (ids: string[], tableKey?: string) =>
  window.sessionStorage.setItem(sessionKey(tableKey), JSON.stringify(ids));

export const useExpandedRows = <TData extends object>(
  table: Table<CleosTableFeaturesType, TData>,
  tableKey?: string,
  canPersist?: boolean
): UseExpandedRowsResult => {
  // See Table/index.tsx's canPersist for why this is gated on an explicit tableKey.
  const persistenceEnabled = Boolean(canPersist);

  const [activeRows, setActiveRows] = useState<string[]>(() => (persistenceEnabled ? loadFromSession(tableKey) : []));

  const persist = useCallback(
    (ids: string[]) => {
      if (persistenceEnabled) saveToSession(ids, tableKey);
    },
    [persistenceEnabled, tableKey]
  );

  const toggleRow = useCallback(
    (rowId: string) => {
      setActiveRows((prev) => {
        const next = prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId];
        persist(next);
        return next;
      });
    },
    [persist]
  );

  const expandNext = useCallback(
    (rowId: string) => {
      const allRows = table.getFilteredRowModel().rows;
      const idx = allRows.findIndex((r) => r.id === rowId);
      if (idx === -1 || idx + 1 >= allRows.length) return;
      const nextId = allRows[idx + 1].id;
      setActiveRows((prev) => {
        const next = [...prev.filter((id) => id !== rowId), nextId];
        persist(next);
        return next;
      });
    },
    [table, persist]
  );

  const closeAll = useCallback(() => {
    setActiveRows([]);
    persist([]);
  }, [persist]);

  return { activeRows, toggleRow, expandNext, closeAll };
};
